// 未ログイン時のお気に入りを LocalStorage に保持するヘルパー。
// 値は ID（正の整数）の配列で、追加順（古い→新しい）で保存する。
// 表示時に「追加した新しい順」へ揃えるための getFavoriteIds(orderedNewestFirst) を用意する。
//
// 同一タブ内の複数コンポーネント（ハートボタン等）が状態を共有できるよう、
// 変更時に購読者へ通知する。別タブの変更は storage イベントで追随する。

export type FavoriteType = "marry_report_events" | "marry_report_photos";

const STORAGE_KEYS: Record<FavoriteType, string> = {
  marry_report_events: "favorites:marry_report_events",
  marry_report_photos: "favorites:marry_report_photos",
};

type Listener = () => void;
const listeners = new Set<Listener>();

// 値が正の整数の配列か検証し、不正値を取り除いて正規化する。
function normalizeIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<number>();
  const result: number[] = [];
  for (const item of value) {
    const id = typeof item === "number" ? item : Number(item);
    if (Number.isInteger(id) && id > 0 && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

function readRaw(type: FavoriteType): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[type]);
    if (!raw) return [];
    return normalizeIds(JSON.parse(raw) as unknown);
  } catch {
    // パース失敗・LocalStorage 不可（プライベートモード等）でも UI を壊さない。
    return [];
  }
}

function writeRaw(type: FavoriteType, ids: number[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS[type], JSON.stringify(ids));
    notify();
    return true;
  } catch {
    return false;
  }
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

// 保存中の ID 配列を返す。orderedNewestFirst=true で「追加した新しい順」に並べ替える。
export function getFavoriteIds(type: FavoriteType, orderedNewestFirst = false): number[] {
  const ids = readRaw(type);
  return orderedNewestFirst ? [...ids].reverse() : ids;
}

export function isFavorite(type: FavoriteType, id: number): boolean {
  return readRaw(type).includes(id);
}

// resolve / sync へ送る両種別の ID を「追加した新しい順」でまとめて取得する。
// 送信時の並び順契約を 1 か所に集約し、呼び出し側での取り違えを防ぐ。
export function getOrderedFavoriteIds(): { reportIds: number[]; photoIds: number[] } {
  return {
    reportIds: getFavoriteIds("marry_report_events", true),
    photoIds: getFavoriteIds("marry_report_photos", true),
  };
}

// 既に存在する場合は何もしない（重複排除）。新規は末尾（最新）に追加する。
export function addFavorite(type: FavoriteType, id: number): boolean {
  if (!Number.isInteger(id) || id <= 0) return false;
  const ids = readRaw(type);
  if (ids.includes(id)) return true;
  return writeRaw(type, [...ids, id]);
}

export function removeFavorite(type: FavoriteType, id: number): boolean {
  const ids = readRaw(type);
  if (!ids.includes(id)) return true;
  return writeRaw(type, ids.filter((existing) => existing !== id));
}

// ログイン直後の同期成功時に、該当種別のお気に入りをすべて削除する。
export function clearFavorites(type: FavoriteType): void {
  try {
    localStorage.removeItem(STORAGE_KEYS[type]);
    notify();
  } catch {
    // 削除できなくても UI 動作には影響しないため握り潰す。
  }
}

// 同一タブ内の変更通知と、別タブからの storage イベントの双方を購読する。
export function subscribeFavorites(listener: Listener): () => void {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    if (event.key === null || Object.values(STORAGE_KEYS).includes(event.key)) {
      listener();
    }
  };
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}
